import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  ChevronLeft,
  ChevronRight,
  ContentCopy,
  InfoOutlined,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";

import {
  EntitlementBucket,
  RestrictionName,
  SortDirection,
  UserRestrictionEntry,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import {
  UsageWindow,
  quotaByBucketType,
  quotaUsage,
  userQuotaByBucket,
} from "./use-org-ai-credit";
import { listOrgUserRestrictions } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { NO_DISPLAY_DATA } from "constants/index";
import { seatTypeLabel } from "constants/seats";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { formatDateTimeDisplay } from "helpers/time";

interface OrgAIUsageSectionProps {
  roleId: string;
  regionCode: string;
  usageWindow: UsageWindow;
  ready: boolean;
}

const PAGE_SIZE = 25;

// Timezone-safe month arithmetic: window boundaries are UTC instants
// (e.g. 2026-08-23T00:00:00Z), so shifting must preserve the UTC day/time.
const addUTCMonths = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  result.setUTCMonth(result.getUTCMonth() + months);
  return result;
};

const COLUMNS: ReadonlyArray<{
  label: string;
  align?: "right";
  width?: string;
  tooltip?: string;
}> = [
  { label: "User", width: "500px" },
  { label: "Seat type", width: "130px" },
  { label: "Used", align: "right", width: "100px" },
  { label: "Limit", align: "right", width: "100px" },
  { label: "Remaining", align: "right", width: "130px" },
  {
    label: "Overage",
    align: "right",
    width: "120px",
    tooltip:
      "Overage is usage beyond a user's free allowance for their seat type. Once that allowance is exhausted, further usage draws from the organization's shared credit pack pool.",
  },
  { label: "Usage", width: "360px" },
];

const displayUserName = (entry: UserRestrictionEntry): string => {
  const { first_name, last_name, email, username } = entry.metadata;
  const fullName = [first_name, last_name].filter(Boolean).join(" ").trim();
  return fullName || email || username || entry.userId;
};

interface StackedUsageBarProps {
  freeUsed: number;
  overageUsed: number;
  freeLimit: number;
  freeRemaining: number;
}

const StackedUsageBar = ({
  freeUsed,
  overageUsed,
  freeLimit,
  freeRemaining,
}: StackedUsageBarProps) => {
  if (freeLimit <= 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {NO_DISPLAY_DATA}
      </Typography>
    );
  }

  // Scale the bar to the greater of the grant or total consumed so overage
  // always gets proportional space — never squeezes to zero width.
  const totalForBar = Math.max(freeLimit, freeUsed + overageUsed);
  const freePct = (freeUsed / totalForBar) * 100;
  const overagePct = (overageUsed / totalForBar) * 100;
  // Mark where the grant ends when overage has pushed us past it.
  const hasOverage = overageUsed > 0;
  const limitMarkerPct = hasOverage ? (freeLimit / totalForBar) * 100 : null;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "2px",
            bgcolor: "success.main",
            flexShrink: 0,
          }}
        />
        <Typography variant="caption">Free used: {freeUsed}</Typography>
      </Box>
      {overageUsed > 0 && (
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              bgcolor: "error.main",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption">Overage used: {overageUsed}</Typography>
        </Box>
      )}
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "2px",
            bgcolor: "grey.400",
            flexShrink: 0,
          }}
        />
        <Typography variant="caption">
          Free remaining: {freeRemaining}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Box sx={{ width: "100%", cursor: "default" }}>
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              height: 6,
              borderRadius: 1,
              bgcolor: "grey.200",
              display: "flex",
              overflow: "hidden",
            }}
          >
            <Box sx={{ width: `${freePct}%`, bgcolor: "success.main" }} />
            <Box sx={{ width: `${overagePct}%`, bgcolor: "error.main" }} />
          </Box>
          {limitMarkerPct !== null && (
            <Box
              sx={{
                position: "absolute",
                top: -1,
                left: `${limitMarkerPct}%`,
                width: "2px",
                height: 8,
                bgcolor: "text.secondary",
                borderRadius: "1px",
                transform: "translateX(-50%)",
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

interface UserCellProps {
  entry: UserRestrictionEntry;
  regionCode: string;
}

const UserCell = ({ entry, regionCode }: UserCellProps) => {
  const { copyToClipboard } = useContext(SnackbarContext);
  const { email } = entry.metadata;

  return (
    <TableCell>
      <Link
        to={{ pathname: `/user/${regionCode}/${entry.userId}` }}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
        data-testid="ai-usage-user-link"
      >
        <Typography
          variant="body2"
          fontWeight="bold"
          color="primary"
          lineHeight={1.3}
        >
          {displayUserName(entry)}
        </Typography>
      </Link>
      {email && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          lineHeight={1.3}
        >
          {email}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={0.25}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.7rem", lineHeight: 1.3 }}
          data-testid="ai-usage-user-id"
        >
          {entry.userId}
        </Typography>
        <Tooltip title="Copy user ID">
          <IconButton
            size="small"
            onClick={() => copyToClipboard(entry.userId)}
            data-testid="ai-usage-copy-user-id"
            aria-label="Copy user ID"
            sx={{ p: 0.25 }}
          >
            <ContentCopy sx={{ fontSize: "0.75rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

const OrgAIUsageSection = ({
  roleId,
  regionCode,
  usageWindow,
  ready,
}: OrgAIUsageSectionProps) => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(true);
  const [entries, setEntries] = useState<UserRestrictionEntry[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);

  // The visible window is the month ending on the plan period end
  // (usageWindow.to), stepped whole months at a time by the arrows. Snap back
  // to the current month whenever the plan period re-anchors (e.g. once the
  // config finishes loading and usageWindow.to changes).
  const anchorTime = usageWindow.to.getTime();
  const [monthOffset, setMonthOffset] = useState(0);
  useEffect(() => setMonthOffset(0), [anchorTime]);

  const windowEnd = addUTCMonths(new Date(anchorTime), monthOffset);
  const windowStart = addUTCMonths(windowEnd, -1);

  const loadUsers = useCallback(
    async (pageNumber: number) => {
      setIsLoading(true);
      const toTime = addUTCMonths(new Date(anchorTime), monthOffset);
      try {
        const response = await listOrgUserRestrictions(
          {
            organizationId: toUUID(roleId),
            restrictionName: RestrictionName.AI_CREDIT_USAGE,
            sortDirection: SortDirection.DESC,
            pageNumber,
            pageSize: PAGE_SIZE,
            fromTime: addUTCMonths(toTime, -1),
            toTime,
          },
          callOptionsHeaders(regionCode, roleId),
        );
        setEntries(response.entries);
        setTotalItems(response.totalItems);
      } catch (err) {
        showError(err as GrpcError);
      } finally {
        setIsLoading(false);
      }
    },
    [roleId, regionCode, showError, anchorTime, monthOffset],
  );

  useEffect(() => {
    if (!ready) return;
    setPage(0);
    loadUsers(1);
  }, [ready, loadUsers]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    loadUsers(newPage + 1);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ pb: 1.5 }}
        >
          <IconButton
            size="small"
            onClick={() => setMonthOffset((o) => o - 1)}
            aria-label="Previous month"
            data-testid="usage-window-prev"
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{ minWidth: 440, textAlign: "center" }}
            data-testid="usage-window-label"
          >
            {`${formatDateTimeDisplay(windowStart)} – ${formatDateTimeDisplay(
              windowEnd,
            )}`}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setMonthOffset((o) => o + 1)}
            aria-label="Next month"
            data-testid="usage-window-next"
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table aria-label="org-ai-user-usage-table" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.label}
                    align={column.align}
                    style={{ width: column.width, fontWeight: "bold" }}
                  >
                    {column.tooltip ? (
                      <Box
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        gap={0.5}
                      >
                        {column.label}
                        <Tooltip title={column.tooltip} placement="top" arrow>
                          <InfoOutlined
                            sx={{
                              fontSize: "0.875rem",
                              color: "text.secondary",
                              cursor: "default",
                            }}
                            data-testid={`col-info-${column.label}`}
                          />
                        </Tooltip>
                      </Box>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>
                    <b>No AI credit usage found for this organisation.</b>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => {
                  const byBucket = userQuotaByBucket(entry.restriction);
                  const freeQuota = quotaByBucketType(
                    byBucket,
                    EntitlementBucket.FREE,
                  );
                  const freeUsed = quotaUsage(freeQuota);
                  const overageUsed = quotaUsage(
                    quotaByBucketType(byBucket, EntitlementBucket.OVERAGE),
                  );
                  const freeLimit = freeQuota?.limit ?? 0;
                  const freeRemaining = freeQuota?.remaining ?? 0;
                  return (
                    <TableRow key={entry.userId} data-testid="ai-usage-row">
                      <UserCell entry={entry} regionCode={regionCode} />
                      <TableCell>
                        {seatTypeLabel(entry.metadata.seat_type) ||
                          NO_DISPLAY_DATA}
                      </TableCell>
                      <TableCell align="right">{freeUsed}</TableCell>
                      <TableCell align="right">{freeLimit}</TableCell>
                      <TableCell align="right">{freeRemaining}</TableCell>
                      <TableCell align="right">{overageUsed}</TableCell>
                      <TableCell>
                        <StackedUsageBar
                          freeUsed={freeUsed}
                          overageUsed={overageUsed}
                          freeLimit={freeLimit}
                          freeRemaining={freeRemaining}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[PAGE_SIZE]}
                  count={totalItems}
                  rowsPerPage={PAGE_SIZE}
                  page={page}
                  onPageChange={handleChangePage}
                  ActionsComponent={RPCTablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default OrgAIUsageSection;
